import * as React from 'react'
import { PLANT_NETWORK_NODE_QUERY_PARAM, plantHref } from '@atbash-labs/plantview-links'
import {
  usePlantSessionPaint,
  type PlantNetworkTreeNode
} from '@axilon-apps/domains-ontology-ui-state'
import {
  PlantTreeNav,
  buildTreeByParentId,
  type ParentIdTreeRow,
  type PlantTreeNavGhost,
  type PlantTreeNavNode,
  type PlantTreeNavSection
} from './PlantTreeNav'
import { collectUnplacedGhosts } from './session-mark-ui'
import { networkIconFor } from './network-icon'
import {
  NETWORK_SECTION_ASSETS,
  NETWORK_SECTION_SEGMENTS,
  NETWORK_SECTION_UNASSIGNED,
  groupNetworkNodesByProject,
  networkTreeHasProjects,
  networkTreeLabel
} from './network-tree-sections'

/** Child kinds that ghost under any Network container in the tree. */
const NETWORK_CHILD_LABELS = new Set(['Asset', 'NetworkSegment', 'NetworkDevice'])

/**
 * Network tab adapter over {@link PlantTreeNav}. Maps the flat
 * `useGetPlantNetworkTreeQuery` node list (Asset → NetworkSegment →
 * NetworkDevice, arbitrarily nested) onto the shared tree rail using the
 * generic `buildTreeByParentId` reducer (like Robotics), since the
 * enterprise/PCN hierarchy is variable-depth. When any row carries an
 * `EmdbProject` membership the rail groups by project (Unassigned last);
 * older graphs without `BELONGS_TO` keep Assets vs Network segments.
 * `childCount` badges the container rows; Compute nodes are not tree rows —
 * they surface as cross-links on each node's detail panel. Selection is
 * `?node=<id>`.
 */
export interface NetworkTreeNavProps {
  nodes: PlantNetworkTreeNode[]
  selectedId?: string
  isLoading?: boolean
  className?: string
}

export function NetworkTreeNav({
  nodes,
  selectedId,
  isLoading,
  className
}: NetworkTreeNavProps): React.ReactElement {
  const { active, filter, markFor, ghostsByContainerId, markById } = usePlantSessionPaint()
  const paintOn = active && filter.enabled

  const sections = React.useMemo((): PlantTreeNavSection[] => {
    const toNavNode = (row: ParentIdTreeRow<PlantNetworkTreeNode>): PlantTreeNavNode => {
      const id = row.node.id
      const ghosts: PlantTreeNavGhost[] = []
      if (paintOn) {
        for (const ghost of ghostsByContainerId.get(id) ?? []) {
          if (!ghost.labels.some((label) => NETWORK_CHILD_LABELS.has(label))) continue
          const view = markFor(ghost.id)
          if (view !== 'deleted' && view !== 'dimmed') continue
          ghosts.push({ id: ghost.id, name: ghost.name ?? ghost.id, view })
        }
      }
      return {
        id,
        href: plantHref(row.node.id, row.node.kind, row.node),
        label: networkTreeLabel(row.node),
        icon: networkIconFor(row.node.kind),
        count: row.node.childCount > 0 ? row.node.childCount : undefined,
        mark: paintOn ? markFor(id) : undefined,
        ghosts: ghosts.length > 0 ? ghosts : undefined,
        groups: [{ nodes: row.children.map(toNavNode) }]
      }
    }

    const treeRoots = (
      bucket: readonly PlantNetworkTreeNode[]
    ): ParentIdTreeRow<PlantNetworkTreeNode>[] =>
      buildTreeByParentId(
        bucket,
        (item) => item.id,
        (item) => item.parentId
      )

    let assetGhosts: PlantTreeNavGhost[] | undefined
    if (paintOn) {
      const liveIds = new Set(nodes.map((item) => item.id))
      const unplaced = collectUnplacedGhosts(markById, ghostsByContainerId, markFor, {
        label: 'Asset',
        liveIds
      })
      assetGhosts =
        unplaced.length > 0
          ? unplaced.map((ghost) => ({
              id: ghost.id,
              name: ghost.name ?? ghost.id,
              view: ghost.view
            }))
          : undefined
    }

    if (networkTreeHasProjects(nodes)) {
      const buckets = groupNetworkNodesByProject(nodes)
      const ghostSectionId = buckets.some((bucket) => bucket.id === NETWORK_SECTION_UNASSIGNED)
        ? NETWORK_SECTION_UNASSIGNED
        : buckets[0]?.id
      return buckets.map((bucket) => ({
        id: bucket.id,
        label: bucket.label,
        nodes: treeRoots(bucket.nodes).map(toNavNode),
        ghosts: bucket.id === ghostSectionId ? assetGhosts : undefined
      }))
    }

    // Split the roots (parentId === null) by kind into two dropdowns: Asset
    // roots vs. everything else (root NetworkSegments, and the rare orphaned
    // root NetworkDevice). Nested segments/devices stay under their container.
    const assetRoots: PlantTreeNavNode[] = []
    const segmentRoots: PlantTreeNavNode[] = []
    for (const row of treeRoots(nodes)) {
      if (row.node.kind === 'Asset') assetRoots.push(toNavNode(row))
      else segmentRoots.push(toNavNode(row))
    }

    return [
      { id: NETWORK_SECTION_ASSETS, label: 'Assets', nodes: assetRoots, ghosts: assetGhosts },
      { id: NETWORK_SECTION_SEGMENTS, label: 'Network segments', nodes: segmentRoots }
    ]
  }, [ghostsByContainerId, markById, markFor, nodes, paintOn])

  return (
    <PlantTreeNav
      heading="Network"
      nodes={[]}
      sections={sections}
      selectParam={PLANT_NETWORK_NODE_QUERY_PARAM}
      selectedId={selectedId}
      isLoading={isLoading}
      loadingText="Loading network…"
      emptyTitle="No network assets"
      emptyHint="Ingest an Experion EMDB from the Ingest tab to populate the enterprise and PCN network tree."
      defaultExpanded
      className={className}
    />
  )
}
